import type { CartItem } from './useCartContext';

export type ReservationStatus = 'pendiente' | 'confirmada' | 'cancelada';
export type PaymentMethod = 'pix' | 'cartao' | 'debito' | 'dinheiro' | 'pendente';
export type PassengerCategory = 'adulto' | 'criança' | 'bebê';

export interface Passenger {
  id: string;
  fullName: string;
  documentType: 'cpf' | 'passaporte';
  documentNumber: string;
  phone: string;
  whatsapp: string;
  category: PassengerCategory;
  boardingStatus?: 'pendente' | 'embarque';
  boardedAt?: string;
}

export interface Reservation {
  id: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerCpf?: string;
  /** Document the lead guest identified with in the checkout (CPF for Brazilians, passport for visitors). */
  documentType?: 'cpf' | 'passport';
  /** CPF digits or upper-cased passport number. */
  documentNumber?: string;
  /** ISO 3166-1 alpha-2 country of the passport, e.g. "AR". */
  nationality?: string;
  /** Jeep tours: where the guide picks the guest up (or the agency, see netlify/lib/cheyTours.mjs). */
  pickupPlace?: string;
  /** Booked through the Pix checkout ('online') or on WhatsApp; only unpaid online ones expire. */
  channel?: 'online' | 'whatsapp';
  /** The guest's language: the site's when they booked online, chosen in the admin otherwise (ticket and emails). */
  language?: 'pt' | 'es' | 'en' | 'fr';
  createdAt: string;
  status: ReservationStatus;
  items: CartItem[];
  totalPeople: number;
  totalPrice: number;
  paymentMethod?: PaymentMethod;
  depositPaid?: number;
  paidTotal?: number;
  /**
   * Payments the admin recorded by hand, one by one (a deposit now, more days later), with their method; paidTotal is
   * what Mercado Pago kept plus these. The one with id 'legacy' is what had been typed as one total before the list.
   */
  manualPayments?: Array<{ id: string; amount: number; method: PaymentMethod; date: string }>;
  /** Confirmed in the admin with "Reservar en chey" unticked (booked there by hand): nothing books it there by itself. */
  cheyManual?: boolean;
  mercadoPagoPaymentId?: string;
  notes?: string;
  passengers?: Passenger[];
  cancellationReason?: string;
  ticketSentAt?: string;
  /** Server-side fields (admin panel): Mercado Pago payments and refunds recorded on the reservation. */
  payments?: Array<{ id: string; amount: number; refunded?: number; status: string; method: 'pix' | 'cartao'; type?: string; installments?: number; date: string }>;
  refunds?: Array<{ paymentId: string; refundId: string; amount: number; date: string }>;
  refundedTotal?: number;
  /** Cancelled and done: what had to be refunded was refunded, anything left was kept on purpose. */
  cancellationSettled?: boolean;
  cancelledAt?: string;
  /**
   * Tours booked in chey (Escuna Aquamarina II, Saco do Mamanguá): the voucher issued in the operator's system;
   * `token` is what its QR carries; `boat` and `departure` are those it was booked on; `tour` (a key of CHEY_TOURS),
   * `date` and `people` what was booked (missing on vouchers linked by hand or issued before they were recorded);
   * `cheyTotal`: what chey charges for it (price per adult, children at half, babies free).
   */
  operatorVoucher?: { system: 'chey'; id?: number; token: string; link: string; issuedAt: string; boatId?: number; boat?: string; departure?: string; meetingPoint?: string; tour?: string; date?: string; people?: number; cheyTotal?: number };
  /** A paid booking no boat had room for (Saco do Mamanguá): the admin accepts it (repasse) or rejects it (refund). */
  operatorReview?: { status: 'pending' | 'accepted' | 'rejected'; since: string; decidedAt?: string; boats?: Array<{ id: number; label: string; seats: number }> };
  source?: 'web' | 'admin';
  /** Last change made in the admin: such a reservation is never removed as unpaid. */
  adminEditedAt?: string;
  updatedAt?: string;
}

const STORAGE_KEY = 'aquamarina_reservations_v1';

export function getReservations(): Reservation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Reservation[]) : [];
  } catch {
    return [];
  }
}

export function saveReservation(reservation: Reservation) {
  // Saving the same id again (visitor retried) replaces it instead of duplicating it
  const reservations = getReservations().filter((item) => item.id !== reservation.id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([reservation, ...reservations]));
}

export function updateReservationStatus(id: string, status: ReservationStatus) {
  const updated = getReservations().map((reservation) =>
    reservation.id === id ? { ...reservation, status } : reservation,
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

export function deleteReservation(id: string) {
  const remaining = getReservations().filter((reservation) => reservation.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
  return remaining;
}

export function updateReservation(id: string, updates: Partial<Reservation>) {
  const updated = getReservations().map((reservation) =>
    reservation.id === id ? { ...reservation, ...updates } : reservation,
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}
